import React, { useCallback, useEffect, useState } from "react";
import TableHeader from "../TableHeader/TableHeader";
import QuickfiltersContainer from "../Quickfilters/QuickfiltersContainer/QuickfiltersContainer";
import { State } from "./interfaces";
import { Container, TopHeader } from "./styles";
import { useTranslation } from "react-i18next";
import { MailInboxQuickfilter, useMailInboxQuickFilter } from "../Quickfilters/MailInboxQuickfilter";
import {
  IncomingMailStatusQuickfilter,
  useIncomingMailStatusQuickFilter,
} from "../Quickfilters/IncomingMailStatusQuickfilter";
import {
  MailClassificationStatusQuickfilter,
  useMailClassificationStatusQuickFilter,
} from "../Quickfilters/MailClassificationStatusQuickfilter";
import {
  MailInboxUploaderQuickfilter,
  useMailInboxUploaderQuickFilter,
} from "../Quickfilters/MailInboxUploaderQuickfilter";
import { PeriodFromToQuickfilter, usePeriodFromToQuickFilter } from "../Quickfilters/PeriodFromToQuickFilter";

import useSearchOptions from "../../custom-hooks/useSearchOptions/useSearchOptions";
import { IncomingMailApiService } from "../../services/incoming-mail/incoming-mail-api.service";
import ManageIncomingMailsList from "./ManageIncomingMailsList/ManageIncomingMailsList";
import CustomTablePagination from "../../reusable-components/Table/CustomTablePagination/CustomTablePagination";
import AddIncomingMail from "../AddIncomingMail/AddIncomingMail";
import { MailInboxDTO } from "src/models/mail-inbox";
import { FormControlLabel, Grid, Switch, Typography } from "@mui/material";
import { IncomingMailFilter } from "src/models/incoming-mail";
import useWindowSize from "src/custom-hooks/useWindowSize/useWindowSize";
import { SPACING } from "src/_globalStore/redux/reducers/Theme/themeConstants";
import { CUSTOM_PAGINATION_ID } from "src/reusable-components/Table/CustomTablePagination/utils";
import LoadingOverlay from "../LoadingOverlay/LoadingOverlay";
import { useAppSelector } from "src/custom-hooks/redux/redux";
import { AUTHORIZATIONS } from "src/utils/constants/authorization-constants";
import {
  CLARIFICATION_STATUS_TYPES,
  INCOMING_MAIL_STATUSES,
  MAIL_CLASSIFICATION_STATUS,
} from "src/utils/constants/constants";
import { IncomingMailStatusRef } from "src/models/incoming-mail-status";
import { MailClassificationStatusRef } from "src/models/mail-classification-status";
import useQuery from "src/custom-hooks/useQuery/useQuery";
import {
  IncomingMailCombinedStatusQuickfilter,
  useIncomingMailCombinedStatusQuickFilter,
} from "../Quickfilters/IncomingMailCombinedStatusQuickfilter";
import { ListSortDirection } from "src/models/api/sort";

const ManageIncomingMailsContainer = () => {
  const { t } = useTranslation();
  const tRP = "pages.manage_mail_page";
  const [state, setState] = useState<State>({
    loading: false,
    error: "",
    items: [],
    page: {
      pageNumber: 0,
      pageSize: 10,
    },
    totalItems: 0,
  });

  const { height } = useWindowSize();
  const query = useQuery();
  const { currentUserAuthorizations } = useAppSelector((state) => state.currentUserReducer);
  //#region mail inbox list
  //get the list of mail inboxes this user has access to their incoming mails
  //this will be used to filter the mail inboxes this user can chose from the quickfilter
  const authorizedMailInboxesForIncoming: string[] =
    currentUserAuthorizations?.allowedMailInboxes
      ?.filter(
        (allowedMailInbox) =>
          allowedMailInbox.value?.includes(AUTHORIZATIONS.MailInboxAccess.CanUploadIncoming) ||
          allowedMailInbox.value?.includes(AUTHORIZATIONS.MailInboxAccess.CanClassifyIncoming) ||
          allowedMailInbox.value?.includes(AUTHORIZATIONS.MailInboxAccess.CanReadIncoming)
      )
      .map((allowedMailInbox) => allowedMailInbox.key) ?? [];
  //#endregion

  const accessedViaLink = query.get("via");
  const DEFAULT_INCOMING_MAIL_STATUS = INCOMING_MAIL_STATUSES.Pending;
  const DEFAULT_CLASSIFICATION_MAIL_STATUS = MAIL_CLASSIFICATION_STATUS.ToBeClassified;

  //#region quickfilters
  const { mailInboxQuickFilter, setMailInboxQuickFilter, selectedMailInbox } = useMailInboxQuickFilter();

  const { incomingMailStatusQuickFilter, setIncomingMailStatusQuickFilter } = useIncomingMailStatusQuickFilter(
    accessedViaLink === "pending" ? [DEFAULT_INCOMING_MAIL_STATUS as IncomingMailStatusRef] : []
  );

  const { mailClassificationStatusQuickFilter, setMailClassificationStatusQuickFilter } =
    useMailClassificationStatusQuickFilter(
      accessedViaLink === "unclassified" ? [DEFAULT_CLASSIFICATION_MAIL_STATUS as MailClassificationStatusRef] : []
    );

  const { mailInboxUploaderQuickFilter, setMailInboxUploaderQuickFilter } = useMailInboxUploaderQuickFilter();

  const { periodFromToQuickFilter, setPeriodFromToQuickFilter } = usePeriodFromToQuickFilter();

  const { incomingMailCombinedStatusQuickFilter, setIncomingMailCombinedStatusQuickFilter } =
    useIncomingMailCombinedStatusQuickFilter();
  //#endregion

  const currentMailInboxAuth = currentUserAuthorizations?.allowedMailInboxes?.find(
    (mailInboxAuth) => mailInboxAuth.key === selectedMailInbox?.id
  )?.value;

  //#region user authorized to
  const canUploadForTheCurrentMailInbox =
    currentMailInboxAuth?.includes(AUTHORIZATIONS.MailInboxAccess.CanUploadIncoming) ?? false;
  const canClassifyForTheCurrentMailInbox =
    currentMailInboxAuth?.includes(AUTHORIZATIONS.MailInboxAccess.CanClassifyIncoming) ?? false;
  const canReadForTheCurrentMailInbox =
    currentMailInboxAuth?.includes(AUTHORIZATIONS.MailInboxAccess.CanReadIncoming) ?? false;
  const isRecipientForTheCurrentMailInbox =
    currentMailInboxAuth?.includes(AUTHORIZATIONS.MailInboxAccess.isRecipient) ?? false;
  //#endregion

  const { searchOptions, handleChangeSearchOptions } = useSearchOptions("");

  //#region load data
  const loadData = useCallback(
    async (signal?: AbortSignal) => {
      if (selectedMailInbox) {
        setState((state) => ({
          ...state,
          loading: true,
        }));
        try {
          const periodFromDate = periodFromToQuickFilter.period?.startDate;
          const periodToDate = periodFromToQuickFilter.period?.endDate;

          const response = await new IncomingMailApiService().loadItems(
            {
              mailInboxIds: [selectedMailInbox.id],
              createdByUsers: [...mailInboxUploaderQuickFilter.mailInboxUploaders.mailInboxUploaderIds],
              statusIds: incomingMailCombinedStatusQuickFilter.incomingMailCombinedStatus.statusIds.filter((statusId) =>
                Object.values(INCOMING_MAIL_STATUSES)
                  .flatMap((_status) => _status.id)
                  .includes(statusId)
              ),
              classificationStatusIds:
                incomingMailCombinedStatusQuickFilter.incomingMailCombinedStatus.statusIds.filter((statusId) =>
                  Object.values(MAIL_CLASSIFICATION_STATUS)
                    .flatMap((_status) => _status.id)
                    .includes(statusId)
                ),
              ...(periodFromDate &&
                periodToDate && {
                  fromDate: {
                    day: periodFromDate.getDate(),
                    month: periodFromDate.getMonth() + 1,
                    year: periodFromDate.getFullYear(),
                  },
                  toDate: {
                    day: periodToDate.getDate(),
                    month: periodToDate.getMonth() + 1,
                    year: periodToDate.getFullYear(),
                  },
                }),
              operatorOR:
                incomingMailCombinedStatusQuickFilter.incomingMailCombinedStatus.statusIds.length > 1
                  ? true
                  : undefined,
            } as IncomingMailFilter,
            searchOptions.query,
            [
              {
                name: "createdAt",
                direction: ListSortDirection.Descending,
              },
              {
                name: "modifiedAt",
                direction: ListSortDirection.Ascending,
              },
            ],
            state.page,
            searchOptions.exactMatch,
            60
          );
          if (!!response.error) {
            setState((state) => ({
              ...state,
              error: response.error,
              items: [],
            }));
          } else {
            setState((state) => ({
              ...state,
              items: response.items,
              totalItems: response.totalItems,
              error: "",
            }));
          }
        } catch (error) {
          setState((state) => ({
            ...state,
            error: error as string,
          }));
        } finally {
          setState((state) => ({
            ...state,
            loading: false,
          }));
        }
      }
    },
    [
      selectedMailInbox,
      // incomingMailStatusQuickFilter,
      // mailClassificationStatusQuickFilter,
      periodFromToQuickFilter,
      mailInboxUploaderQuickFilter,
      searchOptions.exactMatch,
      searchOptions.query,
      state.page,
      incomingMailCombinedStatusQuickFilter.incomingMailCombinedStatus,
    ]
  );
  //#endregion

  useEffect(() => {
    const abortController = new AbortController();

    loadData(abortController.signal);

    return () => abortController.abort();
  }, [state.page]);

  useEffect(() => {
    const abortController = new AbortController();

    if (state.page.pageNumber === 0) {
      loadData(abortController.signal);
    } else {
      setState((state) => ({
        ...state,
        page: {
          ...state.page,
          pageNumber: 0,
        },
      }));
    }
    return () => abortController.abort();
  }, [
    selectedMailInbox,
    // incomingMailStatusQuickFilter,
    // mailClassificationStatusQuickFilter,
    mailInboxUploaderQuickFilter,
    periodFromToQuickFilter,
    searchOptions.exactMatch,
    searchOptions.query,
    incomingMailCombinedStatusQuickFilter,
  ]);

  //#region change page number
  const handleChangePageNumber = useCallback((pageNumber: number) => {
    setState((state) => ({
      ...state,
      page: {
        ...state.page,
        pageNumber: pageNumber,
      },
    }));
  }, []);
  //#endregion

  //#region change page size
  const handleChangePageSize = useCallback((pageSize: number) => {
    setState((state) => ({
      ...state,
      page: {
        ...state.page,
        pageSize: pageSize,
      },
    }));
  }, []);
  //#endregion

  const currHeight =
    height -
    (75 + //add button height
      SPACING[9] * 2 +
      (document.getElementById("header")?.clientHeight ?? 0) +
      SPACING[7] +
      (document.getElementById("MultitabsHorizontal-Portal")?.clientHeight ?? 0) +
      SPACING[7] +
      (document.getElementById(`${CUSTOM_PAGINATION_ID}`)?.clientHeight ?? 0) +
      40);

  return (
    <Container>
      <TopHeader>
        {canUploadForTheCurrentMailInbox && (
          <AddIncomingMail
            mailInbox={(selectedMailInbox as MailInboxDTO) ?? null}
            handleNotifyParent={loadData}
          />
        )}
        <MailInboxQuickfilter
          quickFilters={mailInboxQuickFilter}
          handleChangeQuickFilters={setMailInboxQuickFilter}
          customPropsFilter={{ ids: authorizedMailInboxesForIncoming }}
        />
      </TopHeader>

      <TableHeader
        searchOptions={searchOptions}
        handleChangeSearchOptions={handleChangeSearchOptions}
        quickFilters={
          <QuickfiltersContainer>
            <></>
            {selectedMailInbox && (
              <MailInboxUploaderQuickfilter
                quickFilters={mailInboxUploaderQuickFilter}
                handleChangeQuickFilters={setMailInboxUploaderQuickFilter}
                allUploaders={
                  selectedMailInbox?.authorizedUsers
                    .filter((authUser) => authUser.CanUploadIncoming && !!authUser.user)
                    .map((authUser) => authUser.user!) ?? []
                }
              />
            )}
            <IncomingMailCombinedStatusQuickfilter
              quickFilters={incomingMailCombinedStatusQuickFilter}
              handleChangeQuickFilters={setIncomingMailCombinedStatusQuickFilter}
            />
            {/* <IncomingMailStatusQuickfilter
              quickFilters={incomingMailStatusQuickFilter}
              handleChangeQuickFilters={setIncomingMailStatusQuickFilter}
              {...(accessedViaLink === "pending" && {
                selectedValue: [DEFAULT_INCOMING_MAIL_STATUS.id],
              })}
            />
            <MailClassificationStatusQuickfilter
              quickFilters={mailClassificationStatusQuickFilter}
              handleChangeQuickFilters={setMailClassificationStatusQuickFilter}
              {...(accessedViaLink === "unclassified" && {
                selectedValue: [DEFAULT_CLASSIFICATION_MAIL_STATUS.id],
              })}
            /> */}
            <FormControlLabel
              label={`${t(`${tRP}.show_only_mails_requiring_actions`)}`}
              style={{ margin: "10px 0px" }}
              control={
                <Switch
                  size="small"
                  checked={
                    incomingMailCombinedStatusQuickFilter.incomingMailCombinedStatus.statusIds.length === 2 &&
                    incomingMailCombinedStatusQuickFilter.incomingMailCombinedStatus.statusIds.includes(
                      INCOMING_MAIL_STATUSES.Pending.id
                    ) &&
                    incomingMailCombinedStatusQuickFilter.incomingMailCombinedStatus.statusIds.includes(
                      MAIL_CLASSIFICATION_STATUS.ToBeClassified.id
                    )
                  }
                  onChange={(e) => {
                    if (e.currentTarget.checked) {
                      setIncomingMailCombinedStatusQuickFilter("incomingMailCombinedStatus", {
                        statusIds: [INCOMING_MAIL_STATUSES.Pending.id, MAIL_CLASSIFICATION_STATUS.ToBeClassified.id],
                      });
                    } else {
                      setIncomingMailCombinedStatusQuickFilter("incomingMailCombinedStatus", { statusIds: [] });
                    }
                  }}
                />
              }
            />
            <PeriodFromToQuickfilter
              quickFilters={periodFromToQuickFilter}
              handleChangeQuickFilters={setPeriodFromToQuickFilter}
            />
          </QuickfiltersContainer>
        }
      />
      {!state.loading && state.items.length === 0 && (
        <Grid style={{ display: "flex", justifyContent: "center", padding: "50px" }}>
          <Typography>{`${t(`common_words.no_items`)}`}</Typography>
        </Grid>
      )}

      <>
        <Grid sx={{ width: "100%", height: `${currHeight}px`, overflow: "auto", marginTop: "10px" }}>
          <LoadingOverlay
            error={state.error}
            loading={state.loading}
          >
            <ManageIncomingMailsList
              items={state.items}
              handleNotifyParent={loadData}
              canUpload={canUploadForTheCurrentMailInbox}
              canClassify={canClassifyForTheCurrentMailInbox}
              canRead={canReadForTheCurrentMailInbox}
              isOnlyRecipient={
                !canClassifyForTheCurrentMailInbox &&
                !canReadForTheCurrentMailInbox &&
                !canUploadForTheCurrentMailInbox &&
                isRecipientForTheCurrentMailInbox
              }
            />
          </LoadingOverlay>
        </Grid>
        <CustomTablePagination
          totalItems={state.totalItems}
          pageNumber={state.page.pageNumber}
          pageSize={state.page.pageSize}
          handleChangePageNumber={handleChangePageNumber}
          handleChangePageSize={handleChangePageSize}
        />
      </>
    </Container>
  );
};
export default ManageIncomingMailsContainer;
